"use client";

import { useState, useRef } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import type { PhotoItemType } from "./types";
import MediaGalleryPictograms from "./MediaGalleryPictograms";
import MediaDownloadButton from "./MediaDownloadButton";

interface IPhotoGalleryItemProps {
  item: PhotoItemType;
  handleOpen: (e: React.MouseEvent<Element | any>) => void;
  imageRef: any;
  index: number;
  recentlySeenItems: string[];
  showQrStatus: boolean;
  timeZone?: string;
}

export default function PhotoGalleryItem({
  item,
  handleOpen,
  imageRef,
  index,
  recentlySeenItems = [],
  showQrStatus,
  timeZone,
}: IPhotoGalleryItemProps) {
  const [isThumbnailProcessed, setIsThumbnailProcessed] =
    useState<boolean>(false);
  const isProcessed = item.uploaded;
  const id = `photo_${item.id}`;
  const t = useTranslations();
  const isNew = !item.seenOnUtc && !recentlySeenItems.includes(item.id);
  const retryCountRef = useRef<number>(0);
  const MAX_RETRY_COUNT = 3;
  const TEN_MINUTES_MS = 10 * 60 * 1000;
  const isOldPhoto = item.date && (Date.now() - new Date(item.date).getTime()) > TEN_MINUTES_MS;

  return (
    <div className="relative">
      <div
        className={`absolute inset-0 pointer-events-none select-none z-50 transition-opacity ${isThumbnailProcessed ? "opacity-0" : "opacity-100"}`}
      >
        <Image
          src="/assets/no-picture2.png"
          width={item.width}
          height={item.height}
          className="object-contain aspect-video"
          alt={item.id}
        />
      </div>
      <div
        className={`skeleton bg-[#dadcde] rounded-none absolute inset-0 pointer-events-none select-none z-10 transition-opacity ${isThumbnailProcessed ? "opacity-0" : "opacity-100"}`}
      />

      <Image
        src={item.thumbnail}
        width={item.width}
        height={item.height}
        // className={`lg:object-contain lg:aspect-video w-full lg:w-auto max-w-[100%] select-none ${isProcessed ? "cursor-pointer" : ""}`}
        className={`lg:object-cover w-full lg:w-full select-none ${isProcessed ? "cursor-pointer" : ""}`}
        alt={item.id}
        id={id}
        onClick={(e) => {
          if (isProcessed) {
            handleOpen(e);
          }
        }}
        onLoad={() => setIsThumbnailProcessed(true)}
        onError={() => {
          setIsThumbnailProcessed(false);
          
          // Don't retry for photos older than 10 minutes - they should already exist
          if (isOldPhoto) {
            return;
          }
          
          retryCountRef.current += 1;
          
          // Stop retrying after MAX_RETRY_COUNT attempts
          if (retryCountRef.current >= MAX_RETRY_COUNT) {
            return;
          }

          const element = document.querySelector(`#${id}`);
          if (element) {
            setTimeout(() => {
              const newSrc = `${item.thumbnail}${item.thumbnail.includes('?') ? '&' : '?'}retry=${retryCountRef.current}`;
              element.setAttribute("src", newSrc);
            }, 1500 * retryCountRef.current);
          }
        }}
        ref={imageRef}
        priority={index < 20}
      />

      {!isProcessed ? (
        <div className="absolute z-30 inset-0 flex justify-end items-end pointer-events-none">
          <p className="bg-black/60 text-white py-[2px] px-2 text-xs mb-2 mr-2 font-semibold rounded">
            {t("Gallery.uploadingInProgress")}
          </p>
        </div>
      ) : (
        <>
          <MediaGalleryPictograms
            userConsent={item?.userConsent}
            isNew={isNew}
            showQrStatus={showQrStatus}
            date={item.date}
            timeZone={timeZone}
          />
          <MediaDownloadButton item={item} />
        </>
      )}
    </div>
  );
}
